import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import SettingsGear from '../components/SettingsGear';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, User, Sparkles } from 'lucide-react';

const PastelLeaf = ({ color = "#C8F4D6", style }) => (
  <svg viewBox="0 0 24 24" fill="none" style={style} className="leaf">
    <path d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z"
      fill={color} stroke="#1A1A1A" strokeWidth="1"/>
  </svg>
);

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { darkMode, currentColors } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [c1, c2, c3] = currentColors;
  const leafColors = [c3, c1, c2, c3, c1];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to register');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center p-4 relative">
      {/* Falling leaves */}
      {[...Array(10)].map((_, i) => (
        <PastelLeaf 
          key={i} 
          color={leafColors[i % leafColors.length]}
        />
      ))}

      {/* Settings gear */}
      <SettingsGear />

      {/* Register card */}
      <div className="w-full max-w-md soft-card p-8 sm:p-10 relative z-20" data-testid="register-card">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-block mb-2">
            <img src="https://customer-assets.emergentagent.com/job_mood-diary-calendar/artifacts/sd96v0rx_image.png" alt="Willow Notes" className="w-[36rem] object-contain mx-auto" />
          </div>
          <p className="text-[#4A5568] mt-2 font-medium">{t('startAdventure')}</p>
        </div>

        {error && (
          <div className="bg-[var(--c1)] border-[1.5px] border-[var(--c1-accent)] text-[#1A1A1A] px-4 py-3 rounded-full mb-6 shadow-sm" data-testid="register-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="name" className="text-[#1A1A1A] font-semibold mb-2 block">{t('name')}</Label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A5568]" />
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="What should we call you?"
                required
                className="pl-12 h-14 rounded-full border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:ring-2 focus:ring-[var(--c3-30)] transition-all"
                data-testid="register-name-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email" className="text-[#1A1A1A] font-semibold mb-2 block">{t('email')}</Label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A5568]" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="pl-12 h-14 rounded-full border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:ring-2 focus:ring-[var(--c3-30)] transition-all"
                data-testid="register-email-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password" className="text-[#1A1A1A] font-semibold mb-2 block">{t('password')}</Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A5568]" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a secret password (6+ chars)"
                required
                minLength={6}
                className="pl-12 h-14 rounded-full border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:ring-2 focus:ring-[var(--c3-30)] transition-all"
                data-testid="register-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 soft-btn bg-[var(--c2)] hover:bg-[var(--c2-hover)] text-[#1A1A1A] text-xl"
            data-testid="register-submit-button"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Creating account...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" /> {t('register')}
              </span>
            )}
          </Button>
        </form>

        <p className="text-center mt-8 text-[#4A5568]">
          {t('haveAccount')}{' '}
          <Link to="/login" className="text-[#1A1A1A] font-bold underline decoration-[var(--c2)] decoration-4 hover:decoration-[var(--c1)] transition-colors" data-testid="login-link">
            {t('signIn')}
          </Link>
        </p>
      </div>
    </div>
  );
}
